import type { OnlineEventViewDto } from '@/api/webs'
import type { WebinarRoomRecordingViewDto } from '@/api/webinarRoom'
import { CLOUD_PROVIDER_LABEL, type CloudProvider } from '@/components/common/upload/cloudFile'
import { formatBytes } from '@/utils/fileSize'
import { parseServerDate, parseServerTimestamp } from '@/utils/time'
import { formatMoment, formatRecordingLength, formatWallClockDay, formatWallClockTime, roomHasReplay, sessionEnd } from '@/utils/webinarRoomSetup'

/**
 * What the Room tab's Recording card decides (ORB-1086/1087): which state to draw from the web's recording, its
 * replacement and an upload running in this browser, and the words for each. Whether the recording holds up
 * sign-off is never decided here: that is the server's `OnlineEvent.Recording` item, shown as it comes.
 */

/** `WebinarRoomLimits.MaxRecordingBytes`: 5 GB. The API refuses anything larger when the upload begins. */
export const ROOM_RECORDING_MAX_BYTES = 5 * 1024 * 1024 * 1024

/** MP4 or MOV, what the file picker offers. */
export const ROOM_RECORDING_ACCEPT = '.mp4,.mov,video/mp4,video/quicktime'

/** Every recording and poll control's title once the web is signed off. */
export const LOCK_TITLE = 'Reopen the web to change this.'

export const LEAVE_NOTE_COMPUTER = 'You can keep working in OrbitalX while it uploads. Closing this browser tab stops the upload.'

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = { mp4: 'video/mp4', mov: 'video/quicktime' }
const ACCEPTED_TYPES = new Set(Object.values(CONTENT_TYPE_BY_EXTENSION))

/** The content type to store an MP4 or MOV under, from its name or the browser's guess; null for anything else. */
export function recordingContentType(fileName: string, browserType: string | null | undefined): string | null {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? ''
    if (CONTENT_TYPE_BY_EXTENSION[extension]) return CONTENT_TYPE_BY_EXTENSION[extension]
    return browserType && ACCEPTED_TYPES.has(browserType) ? browserType : null
}

/** Why a file cannot be the recording, or null when it can. The API checks the same again when the upload begins. */
export function recordingFileProblem(file: { name: string; type?: string | null; sizeBytes?: number | null }): string | null {
    if (!recordingContentType(file.name, file.type)) return 'Choose an MP4 or MOV file.'
    if (file.sizeBytes === 0) return 'This file is empty.'
    if (typeof file.sizeBytes === 'number' && file.sizeBytes > ROOM_RECORDING_MAX_BYTES) {
        return `This file is ${formatBytes(file.sizeBytes)}, over the 5 GB limit.`
    }
    return null
}

export type RecordingView = 'empty' | 'uploading' | 'preparing' | 'failed' | 'ready' | 'toolong'

/**
 * The state of the recording in use. An upload of a first recording running in this browser shows as uploading
 * before the reread web says so. "Too long" only picks the layout; the sign-off item is the server's.
 */
export function recordingView(recording: WebinarRoomRecordingViewDto | null | undefined, sessionSeconds: number | null, firstUploadRunning: boolean): RecordingView {
    if (firstUploadRunning && recording?.State !== 'Ready') return 'uploading'
    if (!recording) return 'empty'
    switch (recording.State) {
        case 'Uploading': return 'uploading'
        case 'Preparing': return 'preparing'
        case 'Failed': return 'failed'
        case 'Ready':
            return sessionSeconds !== null && recording.DurationSeconds != null && recording.DurationSeconds > sessionSeconds ? 'toolong' : 'ready'
    }
}

export type ReplacementView = 'uploading' | 'preparing' | 'failed' | null

/** A replacement on its way. A ready one has already been swapped in by the server, so it shows as nothing. */
export function replacementView(pending: WebinarRoomRecordingViewDto | null | undefined, replacementUploadRunning: boolean): ReplacementView {
    if (replacementUploadRunning) return 'uploading'
    switch (pending?.State) {
        case 'Uploading': return 'uploading'
        case 'Preparing': return 'preparing'
        case 'Failed': return 'failed'
        default: return null
    }
}

/**
 * `WebinarRoomLimits.PrepareStuckHours`: a recording still Preparing this long after `PreparingStartedUtc` has nothing
 * working on it any more (a prepare run the platform killed reports nothing), and the API takes Retry for it.
 */
export const PREPARE_STUCK_HOURS = 3

export const PREPARING_TOO_LONG = 'This is taking longer than usual.'

/** `WebinarRoomSchedule.IsPrepareStuck`: Preparing, stamped, and PREPARE_STUCK_HOURS gone since it started. */
export function preparingTooLong(recording: WebinarRoomRecordingViewDto | null | undefined, now: Date): boolean {
    if (recording?.State !== 'Preparing') return false
    const since = parseServerTimestamp(recording.PreparingStartedUtc)
    return since !== null && now.getTime() >= since + PREPARE_STUCK_HOURS * 60 * 60 * 1000
}

/** A replacement still uploading or being prepared holds up sign-off; a failed one does not. */
export function replacementBusy(view: ReplacementView): boolean {
    return view === 'uploading' || view === 'preparing'
}

/** A first upload with nothing ready yet; once a recording is ready, every upload is its replacement. */
export function uploadReplaces(recording: WebinarRoomRecordingViewDto | null | undefined): boolean {
    return recording?.State === 'Ready'
}

/** The card's line under its heading: when the recording plays, and what happens after the session. */
export function recordingSubLine(event: Pick<OnlineEventViewDto, 'StartDateTime' | 'EndDateTime' | 'ModeType'>, recordingReady: boolean, ended: boolean): string {
    const replay = roomHasReplay(event)
    if (ended) {
        return replay ? 'The viewer link plays this recording on demand. Re-runs share it.' : 'Mode is Upcoming, so OrbitalX offers no replay. Re-runs share it.'
    }
    const start = sessionEnd(event.StartDateTime)
    const end = sessionEnd(event.EndDateTime)
    const plays = start
        ? `Plays for everyone at the same point from ${formatWallClockTime(start)}, and nobody can skip ahead.`
        : 'Plays for everyone at the same point, and nobody can skip ahead.'
    const which = recordingReady ? 'this' : 'the'
    const after = replay
        ? end ? `From ${formatWallClockTime(end)} on ${formatWallClockDay(end)} the viewer link plays ${which} recording on demand.` : `After the session the viewer link plays ${which} recording on demand.`
        : end ? `Mode is Upcoming, so OrbitalX offers no replay after ${formatWallClockTime(end)}.` : 'Mode is Upcoming, so OrbitalX offers no replay.'
    return `${plays} ${after} Re-runs share it.`
}

/**
 * The drop zone's second line: the formats, the size limit and, once the session has times, how long it may run,
 * and that the room stays open for live Q&A after a shorter one.
 */
export function dropZoneLimits(sessionSeconds: number | null): string {
    return sessionSeconds === null
        ? 'MP4 or MOV, up to 5 GB.'
        : `MP4 or MOV, up to 5 GB. It can run up to ${formatRecordingLength(sessionSeconds)}, the length of the session. Any time left after the video is live Q&A.`
}

/** "45:00 · 2.8 GB · 1920 × 1080", leaving out what is not known yet. */
export function recordingFacts(recording: WebinarRoomRecordingViewDto): string {
    return [
        recording.DurationSeconds != null ? formatRecordingLength(recording.DurationSeconds) : null,
        recording.SizeBytes != null ? formatBytes(recording.SizeBytes) : null,
        recording.Width && recording.Height ? `${recording.Width} × ${recording.Height}` : null,
    ].filter(Boolean).join(' · ')
}

/** "Uploaded by Dana Okafor, 14 Oct, 11:20 AM", or null when the server did not say who. */
export function uploadedByLine(recording: Pick<WebinarRoomRecordingViewDto, 'UploadedByName' | 'UploadedUtc'>, now: Date): string | null {
    const at = parseServerDate(recording.UploadedUtc)
    if (!recording.UploadedByName) return null
    return at ? `Uploaded by ${recording.UploadedByName}, ${formatMoment(at, now)}` : `Uploaded by ${recording.UploadedByName}`
}

/** A failed recording's facts, or a stuck one's: "2.8 GB · uploaded 14 Oct, 11:20 AM". */
export function failedFacts(recording: WebinarRoomRecordingViewDto, now: Date): string {
    const at = parseServerDate(recording.UploadedUtc)
    return [
        recording.SizeBytes != null ? formatBytes(recording.SizeBytes) : null,
        at ? `uploaded ${formatMoment(at, now)}` : null,
    ].filter(Boolean).join(' · ')
}

const GB = 1024 * 1024 * 1024
const MB = 1024 * 1024

/** "43% · 1.2 of 2.8 GB · about 3 minutes left", in GB for a file of a gigabyte or more and MB below. */
export function transferText(uploadedBytes: number, totalBytes: number, secondsLeft: number | null, withPercent: boolean): string {
    const inGigabytes = totalBytes >= GB
    const amount = (bytes: number) => (inGigabytes ? (bytes / GB).toFixed(1) : String(Math.round(bytes / MB)))
    const parts = [`${amount(uploadedBytes)} of ${amount(totalBytes)} ${inGigabytes ? 'GB' : 'MB'}`]
    if (withPercent) parts.unshift(`${totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0}%`)
    if (secondsLeft !== null) {
        const minutes = Math.max(1, Math.ceil(secondsLeft / 60))
        parts.push(`about ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} left`)
    }
    return parts.join(' · ')
}

/** The source of a recording on its way, as a cloud provider, or null for the computer. */
export function cloudSourceOf(recording: WebinarRoomRecordingViewDto | null | undefined): CloudProvider | null {
    const source = recording?.Source
    return source === 'GoogleDrive' || source === 'OneDrive' || source === 'Dropbox' ? source : null
}

/** What an upload in progress lets the person do meanwhile. A cloud import runs on the server, so the tab can close. */
export function leaveNote(cloudSource: CloudProvider | null): string {
    return cloudSource ? `Importing from ${CLOUD_PROVIDER_LABEL[cloudSource]}. You can close this tab.` : LEAVE_NOTE_COMPUTER
}
